
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { useSubscription } from "../hooks/useSubscription";
import { Loader2, Apple, Smartphone, CreditCard } from "lucide-react";
import { SubscriptionTierBadge } from "./SubscriptionTierBadge";
import { useAuth } from "../contexts/AuthContext";
import { PayPalSubscriptionDetails } from "./PayPalSubscriptionDetails";
import { supabase } from "../integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Badge } from "./ui/badge";
import { openSubscriptionManagement } from "../services/revenueCatService";
import { usePaymentPlatform } from "../contexts/PaymentPlatformContext";

export const SubscriptionDetails = () => {
  const { user } = useAuth();
  const { subscription, isAnyLoading } = useSubscription();
  const [userId, setUserId] = useState<string | null>(null);
  const [paymentProvider, setPaymentProvider] = useState<string | null>(null);
  const [paypalSubscriptionId, setPaypalSubscriptionId] = useState<string | null>(null);
  const [subscriptionSource, setSubscriptionSource] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isNative } = usePaymentPlatform();

  useEffect(() => {
    const getUserData = async () => {
      if (user?.id) {
        try {
          const { data, error } = await supabase
            .from('users')
            .select('id, payment_provider, paypal_subscription_id, subscription_source')
            .eq('id', user.id)
            .single();
          
          if (data && !error) {
            setUserId(data.id);
            setPaymentProvider(data.payment_provider);
            setPaypalSubscriptionId(data.paypal_subscription_id);
            setSubscriptionSource(data.subscription_source);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };

    getUserData();
  }, [user]);
  
  const handlePayPalCancelled = () => {
    // Refresh the component data
    window.location.reload();
  };

  const handleUpgradePlan = () => {
    navigate('/subscription');
  };

  const handleManageNativeSubscription = async () => {
    try {
      await openSubscriptionManagement();
    } catch (error) {
      console.error('Error opening subscription management:', error);
    }
  };

  const getSubscriptionSourceIcon = () => {
    switch (subscriptionSource) {
      case 'apple':
        return <Apple className="h-4 w-4" />;
      case 'google':
        return <Smartphone className="h-4 w-4" />;
      case 'paypal':
      default:
        return <CreditCard className="h-4 w-4" />;
    }
  };

  const getSubscriptionSourceLabel = () => {
    switch (subscriptionSource) {
      case 'apple':
        return t('subscription.sourceApple', { defaultValue: 'Apple App Store' });
      case 'google':
        return t('subscription.sourceGoogle', { defaultValue: 'Google Play' });
      case 'paypal':
      default:
        return t('subscription.sourcePayPal', { defaultValue: 'PayPal' });
    }
  };

  if (isAnyLoading) {
    return (
      <Card>
        <CardContent className="pt-6 flex justify-center items-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const currentTier = subscription?.data?.currentTier || "free";

  // Generate appropriate message based on current tier
  const getSubscriptionMessage = (tier: string) => {
    switch (tier) {
      case "free":
        return t('subscription.freeMessage', { 
          defaultValue: "You are currently on the free tier. Upgrade to access premium features." 
        });
      case "standard":
        return t('subscription.standardMessage', { 
          defaultValue: "You have access to Standard features including up to 3 pets and enhanced medical records." 
        });
      case "premium":
        return t('subscription.premiumMessage', { 
          defaultValue: "You have access to Premium features including up to 5 pets, enhanced storage, and priority support." 
        });
      case "ultimate":
        return t('subscription.ultimateMessage', { 
          defaultValue: "You have access to Ultimate features including up to 15 pets, maximum storage, and 24/7 priority support." 
        });
      default:
        return "Subscription details loading...";
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.currentSubscription')}</CardTitle>
          <CardDescription>{t('subscription.subscriptionDetails')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium mb-1">{t('subscription.subscriptionTier')}</p>
              <SubscriptionTierBadge tier={currentTier as "free" | "standard" | "premium" | "ultimate"} />
            </div>
            
            {/* Upgrade Plan Button with gradient styling */}
            {currentTier === "free" && (
              <Button 
                onClick={handleUpgradePlan}
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold px-6 py-2"
              >
                {t('subscription.upgradePlan')}
              </Button>
            )}
          </div>

          {/* Show subscription source badge for paid tiers */}
          {currentTier !== 'free' && subscriptionSource && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm text-muted-foreground">
                {t('subscription.subscriptionSource', { defaultValue: 'Payment provider:' })}
              </span>
              <Badge variant="secondary" className="flex items-center gap-1">
                {getSubscriptionSourceIcon()}
                {getSubscriptionSourceLabel()}
              </Badge>
            </div>
          )}

          {/* Show provider-specific details */}
          {subscriptionSource === 'paypal' && paypalSubscriptionId ? (
            <PayPalSubscriptionDetails 
              subscriptionId={paypalSubscriptionId}
              onCancelled={handlePayPalCancelled}
            />
          ) : subscriptionSource === 'apple' || subscriptionSource === 'google' ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                {subscriptionSource === 'apple' 
                  ? t('subscription.managedByApple', { defaultValue: 'Subscription managed by Apple. Manage in iOS Settings > Subscriptions.' })
                  : t('subscription.managedByGoogle', { defaultValue: 'Subscription managed by Google Play. Manage in Google Play Store > Subscriptions.' })
                }
              </p>
              {isNative && (
                <Button 
                  variant="outline" 
                  onClick={handleManageNativeSubscription}
                  className="w-full sm:w-auto"
                >
                  {t('subscription.manageSubscription', { defaultValue: 'Manage Subscription' })}
                </Button>
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              {getSubscriptionMessage(currentTier)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
